import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { styled } from '@linaria/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSearchBox, useSearchBoxDatePicker, useSearchBoxGuestPicker } from 'hooks/searchBox';
import noop from 'lodash/noop';
import { getOpenDatePickerType, getSearchBoxValues } from 'modules/searchBox/selectors';
import { setSearchBoxValues } from 'modules/searchBox/slice';
import { DatePickerType, SearchBoxValues, SearchTrigger } from 'modules/searchBox/types';
import { generateAccommodationPageURL } from 'utils/url';
import useBodyScrollLock from '@daedalus/atlas/Overlay/useBodyScrollLock';
import { PopoverPlacement } from '@daedalus/atlas/Popover';
import { ConfigureI18nContext } from '@daedalus/core/src/localization/components/ConfigureI18n';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { roomsSplitToString, roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { RoomConfigType, RoomSplitType } from '@daedalus/core/src/room/types/RoomConfiguration';
import { WithColorPriceDayData } from '@daedalus/core/src/search/modules/ColorPriceCalendar/components/WithColorPriceDayData';
import { usePreviousValue } from '@daedalus/core/src/utils/react/hooks/usePreviousValue';
import { DatePickerPopover } from '@daedalus/shared/src/search/DatePickerPopover';
import { GuestRoomConfigPopover } from '@daedalus/shared/src/search/GuestRoomConfig/GuestRoomConfigPopover';
import { POPOVER_VERTICAL_OFFSET } from './constants';
const _exp = /*#__PURE__*/() => motion.div;
const PopoverBackdrop = /*#__PURE__*/styled(_exp())({
  name: "PopoverBackdrop",
  class: "p1l4qrv",
  propsAsIs: true
});
interface Props {
  currentPopover?: 'datePicker' | 'guestPicker';
  anchorRef: React.RefObject<HTMLDivElement>;
  closePopovers: () => void;
}
export const DateGuestPopovers: React.FC<Props> = ({
  currentPopover,
  closePopovers,
  anchorRef
}) => {
  const dispatch = useDispatch();
  const i18n = useContext(ConfigureI18nContext);
  const previousPopover = usePreviousValue(currentPopover);
  const {
    checkIn,
    checkOut,
    rooms,
    optimizeRooms
  } = useSelector(getSearchBoxValues);
  const [initialValues, setInitialValues] = useState<Pick<SearchBoxValues, 'checkIn' | 'checkOut' | 'rooms'>>();
  useEffect(() => {
    if (!previousPopover && currentPopover) {
      setInitialValues({
        checkIn,
        checkOut,
        rooms
      });
    }
  }, [currentPopover]);
  const {
    onGuestRoomsChange,
    onGuestOptimizeRoomsChange
  } = useSearchBoxGuestPicker();
  const handleGuestRoomsChange = useCallback((roomsSplit: RoomSplitType[]): void => {
    const rooms = roomsSplitToString(roomsSplit);
    onGuestRoomsChange(rooms);
  }, [onGuestRoomsChange]);
  const params = useParams();
  const {
    roomsSplit
  } = roomsToConfigurationObject(rooms);
  const openedDatePickerType = useSelector(getOpenDatePickerType);
  useBodyScrollLock({
    ref: {
      current: document.body as HTMLDivElement
    },
    isEnabled: Boolean(currentPopover) && !process.env.STORYBOOK
  });
  const {
    onDateChange,
    onDatePickerOpen,
    onDatePickerDayClick,
    onDatePickerClose
  } = useSearchBoxDatePicker();
  const {
    doSearch
  } = useSearchBox();
  const onClose = (shouldReset = true) => {
    onDatePickerClose();
    closePopovers();
    if (shouldReset) {
      dispatch(setSearchBoxValues(initialValues));
    }
  };
  const submitSearch = () => {
    doSearch({
      newUrlParams: {
        checkIn,
        checkOut,
        rooms,
        // Preventing from seeing hotelId in the url params list
        hotelId: null
      },
      searchTrigger: SearchTrigger.AccommodationPageDatePicker,
      searchOptions: {
        basePath: generateAccommodationPageURL(params['hotelSlug'], '')
      }
    });
    onClose(false);
  };
  useEffect(() => {
    if (!openedDatePickerType && currentPopover === 'datePicker') {
      onDatePickerOpen(DatePickerType.CheckIn);
    }
  }, [openedDatePickerType, onDatePickerOpen, currentPopover]);
  return <>
      <DatePickerPopover isOpen={currentPopover === 'datePicker'} checkIn={checkIn} checkOut={checkOut} anchorRef={anchorRef} placement={PopoverPlacement.BottomRight} verticalOffset={POPOVER_VERTICAL_OFFSET} weekdaysShort={i18n.weekDayNamesShort} months={i18n.monthNames} firstDayOfWeek={i18n.firstDayOfWeek} onClose={noop} onChange={onDateChange} openedDatePickerType={openedDatePickerType} onDatePickerOpen={onDatePickerOpen} onDatePickerDayClick={onDatePickerDayClick} onSubmit={submitSearch} DayInnerContainer={WithColorPriceDayData} />
      <GuestRoomConfigPopover isOpen={currentPopover === 'guestPicker'} anchorRef={anchorRef} roomsSplit={roomsSplit} placement={PopoverPlacement.BottomRight} verticalOffset={POPOVER_VERTICAL_OFFSET} onClose={noop} onSubmit={submitSearch} onRoomsChange={handleGuestRoomsChange} onOptimizeRoomsChange={onGuestOptimizeRoomsChange} submitButtonMessage={<FormattedMessageWrapper id="update" defaultMessage="Update" />} roomConfigType={optimizeRooms ? RoomConfigType.Optimized : RoomConfigType.Manual} />
      <AnimatePresence>
        {Boolean(currentPopover) && <PopoverBackdrop onClick={() => onClose()} key="ShadowElement" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} />}
      </AnimatePresence>
    </>;
};

require("./DateGuestsPopovers.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./DateGuestsPopovers.tsx");